<template>
  <div>
    <input
      multiple
      ref="fileInput"
      :id="fileinputID"
      :type="'file'"
      :accept="accept"
      @change="didChangeFile"
    />
    <upload-dialogs
      :is-uploading="uploading"
      :upload-progress="progress"
      :show-snackbar="snackbar"
      :upload-name="currentFile.title"
      :upload-size="currentFile.size"
      @hideSnackbar="snackbar = false"
      @didTapCancel="didTapCancel"
    />
    <!-- Dialog de conflito de arquivos -->
    <confirmation-dialog
      v-if="conflictDialog"
      :enabled="conflictDialog"
      :title="'Conflito de arquivos'"
      :confirmationButtonMessage="'Substituir'"
      :message="conflictDialogMessage"
      :listFiles="conflictFiles"
      @abort="setDialog('conflict', false);"
      @confirm="overwriteConflictFiles"
    ></confirmation-dialog>
    <!-- Dialog de erro de upload -->
    <confirmation-dialog
      v-if="errorDialog"
      :enabled="errorDialog"
      :title="'Erro'"
      :message="errorDialogMessage"
      :listFiles="errors"
      :abortButtonMessage="'OK'"
      :dialogColor="'error'"
      @abort="setDialog('error', false);"
    ></confirmation-dialog>

    <fab-icon @click="tapFileInput" />
  </div>
</template>

<script>
import FabIcon from "@/components/FabIcon";
import UploadDialogs from "@/components/dialogs/PhotoUpload";
import exif from "exif-js";
import { storage } from "@/firebase/config";
import ConfirmationDialog from "./ConfirmationDialog";

export default {
  components: { FabIcon, UploadDialogs, ConfirmationDialog },
  props: {
    contentFiles: { required: true },
    fileinputID: { type: String, default: "fileInput" },
    accept: { type: String, default: "" },
    includeMimeTypeFile: { type: Boolean, default: false }
  },

  data: () => ({
    errorDialog: false,
    conflictDialog: false,
    overwriteFiles: false,
    image: {},
    fileReader: {},
    conflictFiles: [],
    uploadOnChange: true,
    currentFile: {},
    snackbar: false,
    files: [],
    resultFiles: [],
    errors: [],
    currentFileIndex: 0,
    uploadTask: null,
    uploading: false,
    progress: 0,
    loading: false
  }),

  computed: {
    collection() {
      return `chapters/${this.$route.params.chapterID}/contents`;
    },
    document() {
      return this.$route.params.contentID;
    },
    conflictDialogMessage() {
      const numberConflicFiles = this.conflictFiles.length;
      const plural = numberConflicFiles > 1;
      let message = plural
        ? `${numberConflicFiles} arquivos já tem correspondentes com os mesmos nomes nesta pasta. Substituí-los irá sobrescrever seus conteúdos.`
        : `Um arquivo com o mesmo nome já existe nesta pasta. Substituí-lo irá sobrescrever seu conteúdo.`;
      return message;
    },
    errorDialogMessage() {
      const numberErrorsFiles = this.errors.length;
      const plural = numberErrorsFiles > 1;
      let message = plural
        ? `Erro detectado no upload de ${numberErrorsFiles} arquivos.`
        : `Erro detectado no upload de um arquivo.`;
      return message;
    }
  },

  methods: {
    readFile(file) {
      return new Promise(resolve => {
        var reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.readAsDataURL(file);
      });
    },

    getFilepath(filename) {
      return this.collection + "/" + this.document + "/content/" + filename;
    },

    rotate(img) {
      return new Promise(resolve => {
        const canvas = document.createElement("canvas");
        exif.getData(img, function() {
          var orientation = exif.getAllTags(this).Orientation;
          if ([5, 6, 7, 8].indexOf(orientation) > -1) {
            canvas.width = img.height;
            canvas.height = img.width;
          } else {
            canvas.width = img.width;
            canvas.height = img.height;
          }
          var ctx = canvas.getContext("2d");
          switch (orientation) {
            case 2:
              ctx.transform(-1, 0, 0, 1, img.width, 0);
              break;
            case 3:
              ctx.transform(-1, 0, 0, -1, img.width, img.height);
              break;
            case 4:
              ctx.transform(1, 0, 0, -1, 0, img.height);
              break;
            case 5:
              ctx.transform(0, 1, 1, 0, 0, 0);
              break;
            case 6:
              ctx.transform(0, 1, -1, 0, img.height, 0);
              break;
            case 7:
              ctx.transform(0, -1, -1, 0, img.height, img.width);
              break;
            case 8:
              ctx.transform(0, -1, 1, 0, 0, img.width);
              break;
            default:
              ctx.transform(1, 0, 0, 1, 0, 0);
          }
          ctx.drawImage(img, 0, 0, img.width, img.height);
          resolve({
            data: canvas.toDataURL("image/jpeg", 0.9),
            width: canvas.width,
            height: canvas.height
          });
        });
      });
    },
    setDefaultConfig() {
      ["conflictFiles", "files", "resultFiles"].forEach(array => {
        this[array] = [];
      });
    },
    didChangeFile(e) {
      this.setDefaultConfig();
      let files = e.target.files || e.dataTransfer.files;
      this.errors = [];
      if (!files.length) return;
      this.files = files;
      if (this.uploadOnChange) {
        this.manageUploads();
      }
    },

    tapFileInput() {
      this.currentFile = {};
      this.uploadTask = null;
      this.snackbar = false;
      let fileInput = document.getElementById(this.$props.fileinputID);
      fileInput.click();
    },

    didTapCancel() {
      if (this.uploadTask) {
        this.uploadTask.cancel();
      }
      this.loading = false;
      this.uploading = false;
      this.files = [];
      this.errors = [];
      this.currentFileIndex = 0;
    },

    setupBatch(files) {
      this.setDefaultConfig();
      this.files = files;
      this.manageUploads();
    },

    uploadImage() {
      const file = this.files[this.currentFileIndex];
      this.rotate(this.image).then(response => {
        this.upload(file, response.data);
      });
    },
    uploadFile(file) {
      if (file.type.includes("image")) {
        this.image = new Image();
        this.image.onload = this.uploadImage;
        this.image.src = this.fileReader.result;
      } else {
        this.upload(file, this.fileReader.result);
      }
    },

    fileIsValid(currentFile) {
      let fileIsValid = true;
      if (!this.overwriteFiles) {
        const filter = file =>
          currentFile.name === file.title && currentFile.type === file.type;
        const fileAlreadyExists = this.contentFiles.filter(filter).length > 0;
        fileIsValid = !fileAlreadyExists;
      }
      return fileIsValid;
    },
    decideAboutFileUpload() {
      const currentFile = this.files[this.currentFileIndex];
      if (this.fileIsValid(currentFile)) this.uploadFile(currentFile);
      else {
        this.conflictFiles.push(currentFile);
        this.currentFileIndex++;
        this.manageUploads();
      }
    },

    decideAboutConflictFiles() {
      this.setDialog("conflict", true);
    },
    decideAboutErrorsFiles() {
      if (this.errors.length > 0) this.setDialog("error", true);
      else this.uploadsFinished();
    },
    stopManageUploads() {
      const resolveConflicts =
        this.conflictFiles.length > 0 && !this.overwriteFiles;
      const priority = resolveConflicts ? "Conflict" : "Errors";
      this[`decideAbout${priority}Files`]();
    },
    manageUploads() {
      if (this.currentFileIndex < this.files.length) {
        this.fileReader = new FileReader();
        this.fileReader.onload = this.decideAboutFileUpload;
        this.fileReader.readAsDataURL(this.files[this.currentFileIndex]);
      } else {
        this.stopManageUploads();
      }
    },
    fileUploaded(url) {
      let fileUploaded = { url: null };
      if (this.currentFile) {
        const uploadDate = new Date();
        fileUploaded = { title: this.currentFile.title, url, uploadDate };
        if (this.includeMimeTypeFile)
          fileUploaded.mimeType = this.currentFile.type;
        fileUploaded.type = this.currentFile.type;
        fileUploaded.overwrite = this.overwriteFiles;
      }
      return fileUploaded;
    },

    uploadSnapShot(snapshot) {
      this.uploading = true;
      this.progress = parseFloat(
        ((snapshot.bytesTransferred / snapshot.totalBytes) * 100).toFixed(0)
      );
    },

    uploadError() {
      const file = this.files[this.currentFileIndex];
      this.errors.push({ name: file.name });
      this.currentFileIndex++;
      this.manageUploads();
    },

    uploadSucess() {
      this.progress = 0;
      this.uploadTask.snapshot.ref.getDownloadURL().then(downloadURL => {
        const fileUpload = this.fileUploaded(downloadURL);
        this.resultFiles.push(fileUpload);
        this.currentFileIndex++;
        this.manageUploads();
      });
    },

    upload(file, data) {
      this.currentFile = {
        title: file.name,
        type: file.type,
        size: (file.size / 1024 / 1024).toFixed(2) + "MB"
      };
      try {
        const storageRef = storage.ref();
        this.uploadTask = storageRef
          .child(this.getFilepath(file.name))
          .putString(data, "data_url");
        this.uploadTask.on(
          "state_changed",
          this.uploadSnapShot,
          this.uploadError,
          this.uploadSucess
        );
      } catch (_) {
        this.uploadError();
      }
    },
    setDialog(dialog, state) {
      this[`${dialog}Dialog`] = state;
      !state && this.uploadsFinished();
    },

    overwriteConflictFiles() {
      this.overwriteFiles = true;
      this.files = this.conflictFiles;
      this.currentFileIndex = 0;
      this.conflictDialog = false;
      this.uploading = true;
      this.manageUploads();
    },

    uploadsFinished() {
      this.image = {};
      this.fileReader = {};
      this.currentFile = {};
      this.uploading = false;
      this.currentFileIndex = 0;
      this.clearFileInput();
      this.$emit("uploadsFinished", this.resultFiles);
      this.snackbar = this.resultFiles.length > 0;
      this.overwriteFiles = false;
      this.setDefaultConfig();
    },

    clearFileInput() {
      this.$refs.fileInput.value = null;
    }
  }
};
</script>